import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowDownRight,
  FiArrowUpRight,
  FiRefreshCw,
  FiSlash,
} from "react-icons/fi";
import { Line } from "react-chartjs-2";
import { fetchEventAnalytics } from "../../../utils/eventApi";
import { compact, shortDay, niceCeiling, DualMeasureDays, RankedBars } from "../../components/DashboardCharts";
import { GROUPS } from "./PointsSummaryTab";

/**
 * The event's monitoring screen: is it healthy right now?
 *
 * Points Summary answers "where did the points come from". This answers the
 * questions asked during a live event: are people still showing up, what are
 * they doing each day, how close is the prize cut-off, and is anyone earning
 * far more than everyone else today.
 *
 * While the event runs it refreshes itself every minute, so it can be left
 * open on a screen.
 *
 * Activity colours are the Points Summary's, by the same entity — games are
 * aqua on both tabs — so the two read as one system.
 */

const REFRESH_MS = 60 * 1000;

const colorOf = (key) => GROUPS.find((g) => g.key === key).color;

/** Activity counts per day: one ledger award each. */
export const ACTIVITY = [
  { key: "check_ins", label: "Check-ins", color: colorOf("checkin") },
  { key: "game_plays", label: "Game plays", color: colorOf("games") },
  { key: "task_completions", label: "Task completions", color: colorOf("tasks") },
  { key: "referral_joins", label: "Referral joins", color: colorOf("referral") },
];

const SOURCE_LABEL = {
  daily_mining: "Mining",
  streak_bonus: "Streak bonus",
  referral: "Referral",
  mini_game: "Games",
  task: "Tasks",
  daily_check_in: "Check-in",
  creator_hub: "Creator Hub",
  other: "Adjustments",
};

/** Earning this many times the day's average puts a user on the watch flag. */
export const WATCH_MULTIPLE = 10;

const fmt = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${Number(n) === 1 ? one : many}`;

/** "9d 3h left" / "3h 12m left" / "12m left". */
export function timeLeft(seconds) {
  if (seconds == null) return null;
  const s = Math.max(0, Number(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

/** "+12 (+8.5%)" against yesterday, or null when there is nothing to compare. */
export function deltaOf(today, yesterday) {
  if (today == null || yesterday == null) return null;
  const diff = today - yesterday;
  const pct = yesterday > 0 ? ` (${diff >= 0 ? "+" : ""}${((diff / yesterday) * 100).toFixed(1)}%)` : "";
  return { diff, text: `${diff >= 0 ? "+" : ""}${fmt(diff)}${pct} vs yesterday` };
}

const countryLabel = (row) => {
  if (row.country === "other") return `Other (${plural(row.countries, "country", "countries")})`;
  if (!row.country) return "Unknown";
  return row.name && row.name !== row.country ? `${row.name} (${row.country})` : row.country;
};

const Delta = ({ delta }) => {
  if (!delta) return null;
  const Icon = delta.diff >= 0 ? FiArrowUpRight : FiArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5">
      <Icon size={11} aria-hidden="true" />
      {delta.text}
    </span>
  );
};

const StatTile = ({ label, value, foot }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-white min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{label}</p>
    <p className="mt-1 text-xl font-bold text-gray-900 truncate">{value}</p>
    {foot && <p className="mt-1 text-[11px] text-gray-500">{foot}</p>}
  </div>
);

const Section = ({ title, hint, children, right }) => (
  <section>
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
      <div>
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</h4>
        {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const UserCell = ({ row, onOpenHistory }) => (
  <div className="min-w-0">
    {onOpenHistory ? (
      <button
        type="button"
        onClick={() => onOpenHistory(row)}
        className="font-medium text-blue-700 hover:underline truncate max-w-[180px] text-left"
        title="Open this participant's points history"
      >
        {row.username || row.email || `#${row.user_db_id}`}
      </button>
    ) : (
      <p className="font-medium text-gray-700 truncate">{row.username || `#${row.user_db_id}`}</p>
    )}
    <p className="text-[10px] text-gray-400 truncate max-w-[180px]">{row.email}</p>
  </div>
);

/** Four activity counts per day, one line each, on one count axis. */
const ActivityChart = ({ days }) => {
  const data = useMemo(
    () => ({
      labels: days.map((d) => shortDay(d.day)),
      datasets: ACTIVITY.map((a) => ({
        label: a.label,
        data: days.map((d) => d[a.key] || 0),
        borderColor: a.color,
        backgroundColor: a.color,
        borderWidth: 2,
        pointRadius: days.length > 20 ? 0 : 3,
        pointHoverRadius: 5,
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        tension: 0.3,
      })),
    }),
    [days]
  );

  const peak = Math.max(0, ...days.flatMap((d) => ACTIVITY.map((a) => d[a.key] || 0)));
  const { max, ticks } = niceCeiling(peak);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    animation: { duration: 240 },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(11,11,11,0.94)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        padding: { top: 8, bottom: 8, left: 10, right: 12 },
        cornerRadius: 6,
        titleFont: { size: 11, weight: "600" },
        bodyFont: { size: 11 },
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        itemSort: (a, b) => b.parsed.y - a.parsed.y,
        callbacks: {
          title: (items) => (items[0] ? `Day ${days[items[0].dataIndex].day_number} · ${shortDay(days[items[0].dataIndex].day)}` : ""),
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: "#e1e0d9" },
        ticks: { color: "#898781", font: { size: 10 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 16 },
      },
      y: {
        beginAtZero: true,
        max,
        grid: { color: "#e1e0d9", drawTicks: false },
        border: { display: false },
        ticks: { color: "#898781", font: { size: 10 }, padding: 6, stepSize: max / ticks, precision: 0, callback: (v) => compact(v) },
      },
    },
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 text-[11px] text-gray-500">
        {ACTIVITY.map((a) => (
          <span key={a.key} className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full" style={{ backgroundColor: a.color }} />
            {a.label}
          </span>
        ))}
      </div>
      <div className="h-48 sm:h-56">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

const AnalyticsTab = ({ eventId, onOpenHistory }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!eventId || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await fetchEventAnalytics(eventId);
      setData(res.data || null);
      setError(null);
      setLoadedAt(new Date());
    } catch (err) {
      // A failed background refresh keeps the last good figures on screen.
      setError(err.message || "Could not load analytics");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const isRunning = data?.event?.status === "running";

  useEffect(() => {
    if (!autoRefresh || !isRunning) return undefined;
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, isRunning, load]);

  const days = useMemo(() => data?.days || [], [data]);
  const newestFirst = useMemo(() => [...days].reverse(), [days]);

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <FiAlertCircle /> {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 py-16 text-center text-xs text-gray-400">Loading analytics…</div>;
  }

  const { event, kpis, leaderboard, countries, watchlist, exchange } = data;
  const today = kpis.today;
  const yesterday = kpis.yesterday;
  const dayWord = isRunning ? "today" : "on the last day";
  const exchangeOrders = Object.values(exchange.by_status).reduce((s, r) => s + r.orders, 0);
  const average = kpis.participants > 0 ? kpis.earned_points / kpis.participants : 0;

  return (
    <div className="p-6 space-y-7">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            Analytics
            {isRunning && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {event.current_day && event.total_days ? `Day ${event.current_day} of ${event.total_days}` : event.status}
            {event.seconds_left != null && ` · ${timeLeft(event.seconds_left)}`}
            {" · Days are UTC"}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          {error && (
            <span className="text-red-600 flex items-center gap-1" title={error}>
              <FiAlertCircle size={12} /> Refresh failed
            </span>
          )}
          {loadedAt && <span>Updated {loadedAt.toLocaleTimeString()}</span>}
          {isRunning && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300"
              />
              Auto-refresh every minute
            </label>
          )}
          <button
            onClick={load}
            disabled={loading}
            title="Refresh now"
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
          >
            <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Health */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatTile
          label="Participants"
          value={fmt(kpis.participants)}
          foot={today ? `+${fmt(today.new_users)} new ${dayWord}` : "Users with any points"}
        />
        <StatTile
          label={isRunning ? "Active today" : "Active, last day"}
          value={today ? fmt(today.active_users) : "—"}
          foot={<Delta delta={deltaOf(today?.active_users, yesterday?.active_users)} />}
        />
        <StatTile
          label={isRunning ? "Points today" : "Points, last day"}
          value={today ? fmt(today.points) : "—"}
          foot={<Delta delta={deltaOf(today?.points, yesterday?.points)} />}
        />
        <StatTile label="Total points" value={fmt(kpis.earned_points)} foot={`${fmt(average)} per participant`} />
        <StatTile
          label="Blocked users"
          value={fmt(kpis.blocked_users)}
          foot={kpis.held_points > 0 ? `${fmt(kpis.held_points)} pts held` : "No points held"}
        />
        <StatTile
          label="Exchange queue"
          value={fmt(kpis.exchange_pending_orders)}
          foot={kpis.exchange_pending_orders === 1 ? "order waiting for review" : "orders waiting for review"}
        />
      </div>

      {days.length === 0 ? (
        <div className="p-8 rounded-xl border border-dashed border-gray-200 text-center text-xs text-gray-400">
          The event has not started yet. Daily figures appear from its first day.
        </div>
      ) : (
        <>
          {/* Daily trend */}
          <Section title="Daily trend" hint="Users who earned any points, and the points they earned, per UTC day">
            <div className="p-4 rounded-xl border border-gray-100 bg-white">
              <DualMeasureDays
                days={days}
                countKey="active_users"
                countLabel="Active users"
                countUnit="users"
                pointsLabel="Points earned"
              />
            </div>
          </Section>

          {/* Activity */}
          <Section title="Activity per day" hint="Rewarded check-ins, game plays, task completions and referral joins">
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <ActivityChart days={days} />
              </div>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-2.5 font-medium">Day</th>
                      <th className="px-4 py-2.5 font-medium text-right">Active</th>
                      <th className="px-4 py-2.5 font-medium text-right">New</th>
                      <th className="px-4 py-2.5 font-medium text-right">Points</th>
                      {ACTIVITY.map((a) => (
                        <th key={a.key} className="px-4 py-2.5 font-medium text-right whitespace-nowrap">
                          {a.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {newestFirst.map((d) => (
                      <tr key={d.day} className="border-b border-gray-50 hover:bg-gray-50/70">
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className="font-medium text-gray-700">Day {d.day_number}</span>
                          <span className="text-gray-400"> · {shortDay(d.day)}</span>
                          {d.day === data.focus_day && isRunning && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-blue-100 text-blue-700">
                              Today
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{fmt(d.active_users)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmt(d.new_users)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">{fmt(d.points)}</td>
                        {ACTIVITY.map((a) => (
                          <td
                            key={a.key}
                            className={`px-4 py-2 text-right tabular-nums ${d[a.key] ? "text-gray-600" : "text-gray-300"}`}
                          >
                            {fmt(d[a.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
        </>
      )}

      {/* Leaderboard */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Section title="Leaderboard" hint="Live order by points; blocked users are left out, as in the app">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <StatTile label="Leader" value={leaderboard.leader_points != null ? fmt(leaderboard.leader_points) : "—"} />
              <StatTile
                label={event.prize_top_n ? `Last prize (#${fmt(event.prize_top_n)})` : "Last prize"}
                value={leaderboard.prize_cutoff_points != null ? fmt(leaderboard.prize_cutoff_points) : "—"}
                foot={
                  leaderboard.prize_cutoff_points == null && event.prize_top_n
                    ? "Fewer ranked users than prizes"
                    : "Points needed to be in the prizes"
                }
              />
              <StatTile label="Ranked users" value={fmt(leaderboard.ranked_users)} />
            </div>
            <div className="rounded-xl border border-gray-100 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-2.5 font-medium">#</th>
                    <th className="px-4 py-2.5 font-medium">User</th>
                    <th className="px-4 py-2.5 font-medium">Country</th>
                    <th className="px-4 py-2.5 font-medium text-right">Points</th>
                    <th className="px-4 py-2.5 font-medium text-right">{isRunning ? "Today" : "Last day"}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.top.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                        Nobody has scored yet.
                      </td>
                    </tr>
                  )}
                  {leaderboard.top.map((r) => (
                    <tr key={r.user_db_id} className="border-b border-gray-50 hover:bg-gray-50/70">
                      <td className="px-4 py-2 font-semibold text-gray-500 tabular-nums">{r.position}</td>
                      <td className="px-4 py-2">
                        <UserCell row={r} onOpenHistory={onOpenHistory} />
                      </td>
                      <td className="px-4 py-2 text-gray-500">{r.country || "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">{fmt(r.total_points)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmt(r.today_points)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        <Section title="Score distribution" hint="Ranked users by total points">
          <div className="p-4 rounded-xl border border-gray-100 bg-white">
            <RankedBars
              emptyText="Nobody has scored yet."
              rows={leaderboard.distribution.map((b) => ({
                key: b.band,
                label: `${b.band} pts`,
                value: b.users,
                foot:
                  leaderboard.ranked_users > 0
                    ? `${((b.users / leaderboard.ranked_users) * 100).toFixed(1)}% of ranked users`
                    : null,
              }))}
            />
          </div>
        </Section>
      </div>

      {/* Watchlist + countries */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Section
            title={isRunning ? "Top earners today" : "Top earners, last day"}
            hint={
              watchlist.average_points > 0
                ? `Against the day's average of ${fmt(watchlist.average_points)} points per active user`
                : "Nobody has earned anything on this day yet"
            }
          >
            <div className="rounded-xl border border-gray-100 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-2.5 font-medium">User</th>
                    <th className="px-4 py-2.5 font-medium">Country</th>
                    <th className="px-4 py-2.5 font-medium text-right">Points</th>
                    <th className="px-4 py-2.5 font-medium text-right">× average</th>
                    <th className="px-4 py-2.5 font-medium">Mostly from</th>
                    <th className="px-4 py-2.5 font-medium text-right">Game plays</th>
                    <th className="px-4 py-2.5 font-medium">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {watchlist.top_earners.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                        No points earned on this day yet.
                      </td>
                    </tr>
                  )}
                  {watchlist.top_earners.map((r) => {
                    const unusual = r.times_average != null && r.times_average >= WATCH_MULTIPLE;
                    return (
                      <tr key={r.user_db_id} className="border-b border-gray-50 hover:bg-gray-50/70">
                        <td className="px-4 py-2">
                          <UserCell row={r} onOpenHistory={onOpenHistory} />
                        </td>
                        <td className="px-4 py-2 text-gray-500">{r.country || "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">{fmt(r.points)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                          {r.times_average != null ? `${r.times_average.toFixed(1)}×` : "—"}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {r.top_source ? SOURCE_LABEL[r.top_source.source] || r.top_source.source : "—"}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmt(r.game_plays)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {r.is_blocked && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700 mr-1">
                              <FiSlash size={10} aria-hidden="true" /> Blocked
                            </span>
                          )}
                          {unusual && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800"
                              title={`Earned ${WATCH_MULTIPLE}× or more of the day's average`}
                            >
                              <FiAlertTriangle size={10} aria-hidden="true" /> Check
                            </span>
                          )}
                          {!r.is_blocked && !unusual && <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        <Section title="Countries" hint="Participants by profile country">
          <div className="p-4 rounded-xl border border-gray-100 bg-white">
            <RankedBars
              emptyText="No participants yet."
              rows={countries.map((c) => ({
                key: c.country || "unknown",
                label: countryLabel(c),
                value: c.users,
                foot: `${fmt(c.points)} pts${
                  kpis.participants > 0 ? ` · ${((c.users / kpis.participants) * 100).toFixed(1)}% of participants` : ""
                }`,
              }))}
            />
          </div>
        </Section>
      </div>

      {/* Exchange */}
      <Section title="Event Exchange" hint="Points spent on exchange orders in this event">
        {exchangeOrders === 0 ? (
          <div className="p-6 rounded-xl border border-dashed border-gray-200 text-center text-xs text-gray-400">
            No exchange orders in this event yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="grid grid-cols-3 gap-3">
              {[
                ["pending", "Pending"],
                ["completed", "Completed"],
                ["rejected", "Rejected"],
              ].map(([key, label]) => (
                <StatTile
                  key={key}
                  label={label}
                  value={fmt(exchange.by_status[key].orders)}
                  foot={`${fmt(exchange.by_status[key].points)} pts`}
                />
              ))}
            </div>
            <div className="rounded-xl border border-gray-100 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-2.5 font-medium">Currency</th>
                    <th className="px-4 py-2.5 font-medium text-right">Orders</th>
                    <th className="px-4 py-2.5 font-medium text-right">Points spent</th>
                    <th className="px-4 py-2.5 font-medium text-right">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {exchange.by_currency.map((c) => (
                    <tr key={c.symbol} className="border-b border-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-700">{c.symbol}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmt(c.orders)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmt(c.points)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">
                        {c.tokens.toLocaleString(undefined, { maximumFractionDigits: 8 })} {c.symbol}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100">
                Pending and completed orders; rejected ones are left out.
              </p>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default AnalyticsTab;
